import React, { useState, useContext, useEffect } from 'react';
import {
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  List,
  ListItem,
  ListItemIcon,
  IconButton,
  CircularProgress,
  styled,
  Divider,
  Alert,
  LinearProgress
} from "@mui/material";
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AWS from 'aws-sdk';
import { v4 as uuidv4 } from 'uuid';

import awsConfig from '../../cfg/aws-config.json';
import { getCaseS3Credentials } from '../../api/cases3credentials';
import { getCaseBucket } from '../../api/casebucket';
import SnackbarContext from '../../context/snackbar-context';
import UserProfileContext from "../../context/userprofilecontext.js";

// Styled components
const DropzoneContainer = styled(Box)(({ theme, isDragActive }) => ({
  border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  backgroundColor: isDragActive
    ? theme.palette.action.hover
    : theme.palette.background.default,
  cursor: 'pointer',
  transition: 'all 0.3s ease-in-out',
  minHeight: '200px',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.light,
  }
}));

const FileItem = styled(ListItem)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(1, 2),
  boxShadow: theme.shadows[1],
}));

export default function UploadStatementsDialog(props) {
  const {
    open = false,
    title = "Upload Files",
    onUpload = () => {},
    onCancel = () => {},
    acceptedFileTypes = {
      'application/pdf': ['.pdf']
    },
    maxFileSize = Infinity, // No limit - removed 20MB restriction
    maxFiles = 0, //10  changed from 10 to unlimited
    largeFileWarningThreshold = 20 * 1024 * 1024 // 20MB warning threshold
  } = props;

  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [fileError, setFileError] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [overallStatus, setOverallStatus] = useState(null);
  const [pendingLargeFiles, setPendingLargeFiles] = useState([]);
  const [showLargeFileWarning, setShowLargeFileWarning] = useState(false);
  const [warningFiles, setWarningFiles] = useState([]);

  const userProfileContext = useContext(UserProfileContext);
  const snackbarContext = useContext(SnackbarContext);

  // Clear state when dialog opens or closes
  useEffect(() => {
    if (!open) {
      // Only reset on close
      return;
    }

    setFiles([]);
    setUploadProgress({});
    setFileError({});
    setIsUploading(false);
    setOverallStatus(null);
    setPendingLargeFiles([]);
    setShowLargeFileWarning(false);
    setWarningFiles([]);
  }, [open]);

  const onDrop = async (acceptedFiles, rejectedFiles) => {
    // Handle rejected files
    if (rejectedFiles && rejectedFiles.length > 0) {
      const errors = {};
      rejectedFiles.forEach((file) => {
        const errorMessage = file.errors[0]?.message || "Invalid file";
        errors[file.file.name] = errorMessage;
      });
      setFileError({...fileError, ...errors});

      if (rejectedFiles.length > 0) {
        snackbarContext.openSnackbarError(
          `${rejectedFiles.length} file(s) could not be added. Please check the file requirements.`
        );
      }
    }

    if (acceptedFiles.length === 0) return;

    // Check for large files that need warning
    const normalFiles = [];
    const largeFiles = [];

    acceptedFiles.forEach(file => {
      if (file.size > largeFileWarningThreshold) {
        largeFiles.push(file);
      } else {
        normalFiles.push(file);
      }
    });

    // Add normal files immediately
    if (normalFiles.length > 0) {
      setFiles(prevFiles => [...prevFiles, ...normalFiles]);
    }

    // Handle large files with warning
    if (largeFiles.length > 0) {
      setWarningFiles(largeFiles);
      setPendingLargeFiles(largeFiles);  // Store large files to add after confirmation
      setShowLargeFileWarning(true);
    }
  };

  const removeFile = (fileToRemove) => {
    setFiles(files.filter(file => file !== fileToRemove));

    // Clean up any progress or errors for this file
    setUploadProgress(prev => {
      const updated = {...prev};
      delete updated[fileToRemove.name];
      return updated;
    });

    setFileError(prev => {
      const updated = {...prev};
      delete updated[fileToRemove.name];
      return updated;
    });
  };

  const confirmLargeFileUpload = () => {
    // Add pending files to the files list
    setFiles(prevFiles => [...prevFiles, ...pendingLargeFiles]);
    setPendingLargeFiles([]);
    setShowLargeFileWarning(false);
    setWarningFiles([]);
  };

  const cancelLargeFileUpload = () => {
    setShowLargeFileWarning(false);
    setWarningFiles([]);
    // Don't add pending files
    setPendingLargeFiles([]);
  };

  const uploadFiles = async () => {
    if (files.length === 0) return;

    setIsUploading(true);
    setOverallStatus("uploading");

    try {
      const caseCredentialsResponse = await getCaseS3Credentials();
      if (caseCredentialsResponse == null) {
        throw new Error('Could not get credentials to upload files');
      }
      const caseCredentials = caseCredentialsResponse.Credentials;

      const caseBucketResponse = await getCaseBucket();
      if (caseBucketResponse == null) {
        throw new Error('Could not get location to upload files');
      }
      const caseBucket = caseBucketResponse.bucketName;

      // Upload all files in parallel
      const uploadPromises = files.map(file =>
        uploadFile(caseBucket, caseCredentials, file)
      );

      await Promise.all(uploadPromises);

      setOverallStatus("success");
      snackbarContext.openSnackbarSuccess('Files uploaded successfully');
      onUpload(files);
    } catch (error) {
      setOverallStatus("error");
      snackbarContext.openSnackbarError('There was an error uploading the files');
      console.error('Error uploading files:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const uploadFile = async (s3_bucket, credentials, file) => {
    try {
      const region = awsConfig.default_region;

      if (file.type !== 'application/pdf') {
        setFileError((prevError) => ({
          ...prevError,
          [file.name]: "File is not PDF",
        }));
        return;
      }

      const orgID = userProfileContext?.userProfile?.currentOrg?.orgID;
      const caseID = userProfileContext?.userProfile?.currentCase?.caseID;
      const userID = userProfileContext?.userProfile?.currentUser?.userID;

      if (!caseID || !userID) {
        throw new Error("caseID and userID are required properties");
      }

      const uuid = uuidv4();
      const originalFileName = file.name;
      const folderPath = 'uploads/';
      const key = `${folderPath}${uuid}_${originalFileName}`;

      const metadata = {
        'original-filename': originalFileName,
        'uploaded-by': userID
      };

      // Build tagging — orgID is optional (not present for Customer:User)
      const tagParts = [`CaseID=${caseID}`, `UserID=${userID}`];
      if (orgID) tagParts.push(`OrgID=${orgID}`);
      const tagging = tagParts.join('&');

      const params = {
        Bucket: s3_bucket,
        Key: key,
        ContentType: file.type,
        Body: file,
        Tagging: tagging,
        Metadata: metadata,
      };

      const options = {
        partSize: 10 * 1024 * 1024,
        queueSize: 1,
      };

      const s3 = new AWS.S3({
        apiVersion: "latest",
        region: region,
        credentials: {
          accessKeyId: credentials.AccessKeyId,
          secretAccessKey: credentials.SecretAccessKey,
          sessionToken: credentials.SessionToken,
        },
      });

      const managedUpload = s3.upload(params, options);

      managedUpload.on('httpUploadProgress', function (progress) {
        const percentCompleted = Math.round((progress.loaded * 100) / progress.total);
        setUploadProgress((prevProgress) => ({
          ...prevProgress,
          [file.name]: percentCompleted,
        }));
      });

      await managedUpload.promise();

      return true;
    } catch (error) {
      setFileError((prevError) => ({
        ...prevError,
        [file.name]: error.message || "Upload failed",
      }));
      console.error('Error uploading file:', error);
      return false;
    }
  };

  const handleCancel = () => {
    if (isUploading) {
      // Confirm cancel if uploads in progress
      if (window.confirm('Uploads in progress. Are you sure you want to cancel?')) {
        setFiles([]);
        setFileError({});
        setUploadProgress({});
        onCancel();
      }
    } else {
      setFiles([]);
      setFileError({});
      setUploadProgress({});
      onCancel();
    }
  };

  const allFilesUploaded = files.length > 0 &&
    files.every((file) => uploadProgress[file.name] === 100 || fileError[file.name] != null);

  const isDialogOpen = open &&
    userProfileContext.userProfile != null &&
    userProfileContext.userProfile.currentCase != null;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    maxSize: maxFileSize,
    maxFiles: maxFiles,
    disabled: isUploading,
  });

  const getFileUploadStatus = (fileName) => {
    if (fileError[fileName]) return "error";
    if (uploadProgress[fileName] === 100) return "complete";
    if (uploadProgress[fileName] > 0) return "uploading";
    return "pending";
  };

  return (
    <Dialog
      open={isDialogOpen}
      onClose={isUploading ? undefined : handleCancel}
      fullWidth
      maxWidth="sm"
      aria-labelledby="upload-dialog-title"
    >
      <DialogTitle id="upload-dialog-title">
        {title}
      </DialogTitle>

      <DialogContent>
        {/* Error message if overall upload fails */}
        {overallStatus === "error" && (
          <Alert severity="error" sx={{ mb: 2 }}>
            There was an error uploading your files. Please try again.
          </Alert>
        )}

        {/* Success message when all uploads complete */}
        {overallStatus === "success" && (
          <Alert severity="success" sx={{ mb: 2 }}>
            All files uploaded successfully!
          </Alert>
        )}

        {/* Dropzone */}
        {!allFilesUploaded && !isUploading && (
          <DropzoneContainer
            {...getRootProps()}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon
              fontSize="large"
              color={isDragActive ? "primary" : "action"}
              sx={{ mb: 2 }}
            />
            {isDragActive ? (
              <Typography variant="body1" color="primary">
                Drop the files here...
              </Typography>
            ) : (
              <>
                <Typography variant="body1">
                  Drag and drop PDF files here, or click to select
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                  Accept PDF files up to {maxFileSize/1024/1024}MB
                </Typography>
              </>
            )}
          </DropzoneContainer>
        )}

        {/* Large File Warning Dialog */}
        {showLargeFileWarning && (
          <Dialog
            open={showLargeFileWarning}
            onClose={cancelLargeFileUpload}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              <WarningAmberIcon color="warning" sx={{ mr: 1, verticalAlign: 'middle' }} />
              Large File Warning
            </DialogTitle>
            <DialogContent>
              <Alert severity="warning" sx={{ mb: 2 }}>
                The following file(s) exceed {largeFileWarningThreshold / 1024 / 1024}MB and may take longer to process.
              </Alert>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Files exceeding threshold:
              </Typography>
              <Box sx={{ mb: 2 }}>
                {warningFiles.map((file, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                    <InsertDriveFileIcon color="primary" sx={{ mr: 2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2">{file.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(file.size / 1024 / 1024)} MB
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              <Typography variant="subtitle2" sx={{ mt: 2, color: 'text.secondary' }}>
                Normal files have been added immediately. Large files will be added upon confirmation.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={cancelLargeFileUpload} color="inherit">
                Cancel
              </Button>
              <Button onClick={confirmLargeFileUpload} color="warning" variant="contained">
                Confirm Upload
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* File List */}
        {files.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" component="div" sx={{ mb: 1 }}>
              {files.length} file{files.length !== 1 ? 's' : ''}
            </Typography>
            <List>
              {files.map((file, index) => {
                const status = getFileUploadStatus(file.name);

                return (
                  <FileItem key={index}>
                    <ListItemIcon>
                      {status === "complete" ? (
                        <CheckCircleIcon color="success" />
                      ) : status === "error" ? (
                        <ErrorIcon color="error" />
                      ) : (
                        <InsertDriveFileIcon color="primary" />
                      )}
                    </ListItemIcon>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {file.name}
                      </Typography>
                      {fileError[file.name] ? (
                        <Typography variant="caption" color="error.main" display="block">
                          {fileError[file.name]}
                        </Typography>
                      ) : uploadProgress[file.name] ? (
                        <Box sx={{ width: '100%', mt: 0.5 }}>
                          <LinearProgress
                            variant="determinate"
                            value={uploadProgress[file.name]}
                            color={uploadProgress[file.name] === 100 ? "success" : "primary"}
                          />
                          <Typography variant="caption" display="block" sx={{ mt: 0.25 }}>
                            {uploadProgress[file.name]}%
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {Math.round(file.size / 1024)} KB
                        </Typography>
                      )}
                    </Box>

                    {!isUploading && status !== "complete" && (
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => removeFile(file)}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </FileItem>
                );
              })}
            </List>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button
          onClick={handleCancel}
          color="inherit"
          disabled={isUploading}
        >
          {allFilesUploaded ? "Close" : "Cancel"}
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {files.length > 0 && !allFilesUploaded && (
            <Button
              variant="contained"
              color="primary"
              startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
              onClick={uploadFiles}
              disabled={isUploading || files.length === 0}
            >
              {isUploading ? "Uploading..." : "Upload Files"}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}